// iter147 — Cross-page sync bus for Daily Entry writes.
//
// Any page that consumes daily_entries (MonthlyMDCF / MDM Bill, ReportsHub,
// Hand Receipts, Ingredient Reports, Vendor bills, CCH stock etc.) can call
// `useDailyEntriesVersion()` in the same tab and it will re-render whenever
// a Daily Entry is saved, deleted, or bulk-changed — so users never have to
// manually refresh to see the latest numbers.
//
// Cross-tab sync is handled via the `storage` event (localStorage). When
// admin/school edits an entry in one tab, all other open tabs pick it up.

import { useEffect, useState } from "react";

const STORAGE_KEY = "daily-entries-version";
let _local = Number(sessionStorage.getItem(STORAGE_KEY) || 0);
const _listeners = new Set();

/** Call after any successful create/update/delete of a daily entry. */
export function bumpDailyEntriesVersion() {
  _local += 1;
  try {
    // localStorage triggers "storage" event in OTHER tabs (same origin).
    localStorage.setItem(STORAGE_KEY, String(_local));
    sessionStorage.setItem(STORAGE_KEY, String(_local));
  } catch (_e) { /* private mode or storage disabled — no-op */ }
  _listeners.forEach((fn) => fn(_local));
}

/** Live-updating version counter. Consumers refetch when it changes. */
export function useDailyEntriesVersion() {
  const [v, setV] = useState(_local);
  useEffect(() => {
    const fn = (next) => setV(next);
    _listeners.add(fn);
    // Cross-tab: react to writes from other tabs.
    const onStorage = (ev) => {
      if (ev.key !== STORAGE_KEY) return;
      const next = Number(ev.newValue || 0);
      if (Number.isFinite(next) && next > _local) {
        _local = next;
        setV(next);
      }
    };
    window.addEventListener("storage", onStorage);
    // Also refetch when the user comes back to this tab after doing edits
    // elsewhere — a common cause of "why doesn't it show fresh data?".
    const onFocus = () => setV((cur) => cur + 0.001);
    window.addEventListener("focus", onFocus);
    return () => {
      _listeners.delete(fn);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
  return v;
}
